export interface Suggestion {
  id: string;
  text: string;
  category?: string;
}

export class SuggestionsHandler {
  private defaultSuggestions: Suggestion[] = [
    // Game Basics
    { id: 'game-name', text: 'What is the name of the game?', category: 'basics' },
    { id: 'developer', text: 'Who developed this game?', category: 'basics' },
    
    // How to Play
    { id: 'how-to-play', text: 'How do you play Sudoku?', category: 'gameplay' },
    { id: 'sudoku-rules', text: 'Can you explain the Sudoku rules?', category: 'gameplay' },
    
    // Game Modes
    { id: 'game-modes', text: 'What modes are available in the game?', category: 'modes' },
    { id: 'versus-mode', text: 'What is Versus Mode?', category: 'modes' },
    { id: 'single-mode', text: 'What is Single Mode?', category: 'modes' },
    
    // Multiplayer & Challenges
    { id: 'challenge-random', text: 'How do I challenge a random player?', category: 'multiplayer' },
    { id: 'challenge-friends', text: 'How do I challenge my friends?', category: 'multiplayer' },
    { id: 'add-players', text: 'Can I add players after a match?', category: 'multiplayer' },
    
    // Rewards & Bonuses
    { id: 'friend-rewards', text: 'How do friend rewards work?', category: 'rewards' },
    { id: 'daily-rewards', text: 'Are there daily rewards?', category: 'rewards' },
    { id: 'extra-rewards', text: 'Can I earn extra rewards?', category: 'rewards' },
    
    // Progress & Statistics
    { id: 'past-games', text: 'Can I see my past games?', category: 'stats' },
    { id: 'leaderboard', text: 'Is there a leaderboard?', category: 'stats' },
    { id: 'win-rate', text: 'Can I check my win rate and match record?', category: 'stats' },
    
    // Gameplay Mechanics
    { id: 'turns-work', text: 'How do turns work in Versus Mode?', category: 'mechanics' },
    { id: 'inactive-player', text: 'What happens if a player is inactive?', category: 'mechanics' },
    { id: 'scoring-system', text: 'How does the scoring system work?', category: 'mechanics' },
    
    // Tips & Strategy
    { id: 'scoring-tips', text: 'Any tips for scoring better?', category: 'strategy' },
    { id: 'maximize-points', text: 'How can I maximize my points?', category: 'strategy' },
    { id: 'best-strategy', text: 'What is the best strategy for scoring high?', category: 'strategy' },
    
    // Match Management
    { id: 'quit-match', text: 'Can I quit a match?', category: 'management' },
    { id: 'pause-game', text: 'Can I pause a game?', category: 'management' },
    
    // User Profile
    { id: 'username-purpose', text: 'What is the username for?', category: 'profile' },
    { id: 'change-username', text: 'How do I change my username?', category: 'profile' }
  ];

  /**
   * Get random startup suggestions
   */
  public getStartupSuggestions(count: number = 3): Suggestion[] {
    const shuffled = [...this.defaultSuggestions].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  }

  /**
   * Get contextual suggestions based on user input
   * This would typically call your backend API, but for now returns filtered suggestions
   */
  public async getContextualSuggestions(userInput: string, count: number = 3): Promise<Suggestion[] | null> {
    try {
      // TODO: Replace this with actual API call to your backend
      // const response = await fetch('/api/suggestions', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ userInput })
      // });
      // if (!response.ok) return null;
      // return await response.json();

      // For now, return filtered suggestions based on keywords
      return this.getFilteredSuggestions(userInput, count);
    } catch (error) {
      console.error('Error getting contextual suggestions:', error);
      return null;
    }
  }

  /**
   * Get suggestions by category
   */
  public getSuggestionsByCategory(category: string, count?: number): Suggestion[] {
    const categorySuggestions = this.defaultSuggestions.filter(s => s.category === category);
    if (count) {
      return categorySuggestions.slice(0, count);
    }
    return categorySuggestions;
  }

  /**
   * Get all available categories
   */
  public getCategories(): string[] {
    const categories = new Set(this.defaultSuggestions.map(s => s.category).filter(Boolean));
    return Array.from(categories);
  }

  /**
   * Search suggestions by text content
   */
  public searchSuggestions(query: string): Suggestion[] {
    const lowerQuery = query.toLowerCase();
    return this.defaultSuggestions.filter(s => 
      s.text.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Filter suggestions based on user input keywords (fallback/demo implementation)
   */
  private getFilteredSuggestions(userInput: string, count: number): Suggestion[] {
    const input = userInput.toLowerCase();
    const keywords = input.split(' ');
    
    // Define keyword mappings to categories
    const categoryMappings: { [key: string]: string[] } = {
      'gameplay': ['play', 'rules', 'how', 'sudoku', 'game'],
      'modes': ['mode', 'versus', 'single', 'multiplayer'],
      'multiplayer': ['friend', 'challenge', 'player', 'opponent', 'match'],
      'rewards': ['reward', 'bonus', 'daily', 'earn', 'points'],
      'stats': ['score', 'record', 'history', 'leaderboard', 'statistics', 'win'],
      'mechanics': ['turn', 'scoring', 'inactive', 'system'],
      'strategy': ['tip', 'strategy', 'better', 'maximize', 'best'],
      'management': ['quit', 'pause', 'stop', 'exit'],
      'profile': ['username', 'name', 'profile', 'account']
    };

    // Find relevant categories based on keywords
    const relevantCategories: string[] = [];
    Object.entries(categoryMappings).forEach(([category, categoryKeywords]) => {
      if (keywords.some(keyword => categoryKeywords.some(ck => ck.includes(keyword) || keyword.includes(ck)))) {
        relevantCategories.push(category);
      }
    });

    // Get suggestions from relevant categories
    let filteredSuggestions: Suggestion[] = [];
    if (relevantCategories.length > 0) {
      filteredSuggestions = this.defaultSuggestions.filter(s => 
        s.category && relevantCategories.includes(s.category)
      );
    }

    // If no specific matches, return random suggestions
    if (filteredSuggestions.length === 0) {
      filteredSuggestions = [...this.defaultSuggestions];
    }

    // Shuffle and return requested count
    const shuffled = filteredSuggestions.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  }
}